import { Box, Chip, Stack, Theme, Typography } from '@mui/material';
import { TFunction } from 'i18next';
import { ReactNode } from 'react';
import {
  FloodCategory,
  FloodCategoryLevel,
  FloodImpact,
} from 'features/waterLevel/waterLevel.types';
import {
  floodCategoryColorsSolid,
  WATER_LEVEL_LINE_COLOR,
} from '../../waterLevel.constants';
import {
  getFloodCategoryFromLevel,
  shouldDisplayWaterLevelForecast,
  waterLevelsEqualForDisplay,
} from '../../waterLevel.utils';

const MARKER_LINE_BOLD_PX = 3;
const MARKER_LINE_THIN_PX = 2;
// Extra space to compensate for container padding/gaps to achieve full-width
export const MARKER_FULL_BLEED_WIDTH = 'calc(100% + 16px)';

export type LevelIndicatorItem = {
  key: string;
  level: number;
  backgroundColor: string;
  useContrastText: boolean;
  body: ReactNode;
  /** Impact row that matches a flood-stage threshold level. */
  isThreshold?: boolean;
  /** Standalone current/forecast marker row (not an impact). */
  isMarker?: boolean;
  /** Current/forecast chip + line merged into this impact row (same display level). */
  markerSlot?: ReactNode;
  /** Whether this row includes a current-level marker (standalone or merged). */
  hasCurrentLevelMarker?: boolean;
};

export type GetLevelIndicatorItemsArgs = {
  floodImpacts: FloodImpact[];
  floodCategories: FloodCategoryLevel[];
  currentLevel: number | undefined;
  forecastPeakLevel: number | undefined;
  t: TFunction;
};

type LevelMarkerStyle = Pick<
  LevelIndicatorItem,
  'backgroundColor' | 'useContrastText'
> & {
  lineColor: string;
  lineThicknessPx: number;
};

type BuildLevelMarkerItemArgs = {
  key: LevelIndicatorItem['key'];
  level: number;
  label: string;
  lineVariant?: 'solid' | 'dashed';
  floodCategories: FloodCategoryLevel[];
  getCategoryForLevel: (targetLevel: number) => FloodCategory;
};

const CurrentLevelBody = ({
  label,
  lineVariant = 'solid',
  lineColor = WATER_LEVEL_LINE_COLOR,
  lineThicknessPx = MARKER_LINE_THIN_PX,
}: {
  label: string;
  lineVariant?: 'solid' | 'dashed';
  lineColor?: string;
  lineThicknessPx?: number;
}): JSX.Element => (
  <Stack
    direction="row"
    alignItems="center"
    sx={{ flexGrow: 1, width: MARKER_FULL_BLEED_WIDTH }}
  >
    <Chip
      size="small"
      variant="watchduty-basic"
      label={label}
      sx={(theme: Theme) => ({
        backgroundColor: WATER_LEVEL_LINE_COLOR,
        color: theme.palette.getContrastText(WATER_LEVEL_LINE_COLOR),
        borderTopLeftRadius: 0,
        borderBottomLeftRadius: 0,
      })}
    />
    <Box
      sx={{
        flexGrow: 1,
        alignSelf: 'center',
        height: 0,
        borderBottom: `${lineThicknessPx}px ${lineVariant} ${lineColor}`,
      }}
    />
  </Stack>
);

export const getFloodCategoryForLevel = (
  level: number,
  floodCategories: FloodCategoryLevel[],
): FloodCategory => getFloodCategoryFromLevel(level, floodCategories) ?? 'none';

const getLevelMarkerStyle = (
  level: number,
  floodCategories: FloodCategoryLevel[],
  getCategoryForLevel: (targetLevel: number) => FloodCategory,
): LevelMarkerStyle => {
  if (floodCategories.length === 0) {
    return {
      backgroundColor: WATER_LEVEL_LINE_COLOR,
      useContrastText: true,
      lineColor: WATER_LEVEL_LINE_COLOR,
      lineThicknessPx: MARKER_LINE_BOLD_PX,
    };
  }
  const rawCategory = getCategoryForLevel(level);
  const category: FloodCategory =
    rawCategory === 'none' ? 'normal' : rawCategory;
  const backgroundColor = floodCategoryColorsSolid[category];
  const atFloodStageLevel = floodCategories.some((fc) =>
    waterLevelsEqualForDisplay(fc.level, level),
  );
  return {
    backgroundColor,
    useContrastText: true,
    lineColor: atFloodStageLevel ? backgroundColor : WATER_LEVEL_LINE_COLOR,
    lineThicknessPx: atFloodStageLevel
      ? MARKER_LINE_THIN_PX
      : MARKER_LINE_BOLD_PX,
  };
};

const buildLevelMarkerBody = ({
  level,
  label,
  lineVariant,
  floodCategories,
  getCategoryForLevel,
}: Omit<BuildLevelMarkerItemArgs, 'key'>): JSX.Element => {
  const { lineColor, lineThicknessPx } = getLevelMarkerStyle(
    level,
    floodCategories,
    getCategoryForLevel,
  );
  const variant = lineVariant ?? 'solid';
  return (
    <CurrentLevelBody
      label={label}
      lineVariant={variant}
      lineColor={lineColor}
      lineThicknessPx={
        variant === 'dashed' ? MARKER_LINE_THIN_PX : lineThicknessPx
      }
    />
  );
};

const insertLevelIndicatorItem = (
  items: LevelIndicatorItem[],
  item: LevelIndicatorItem,
): LevelIndicatorItem[] => {
  const insertIndex = items.findIndex((i) => i.level > item.level);
  if (insertIndex === -1) {
    return [...items, item];
  }
  return [...items.slice(0, insertIndex), item, ...items.slice(insertIndex)];
};

const buildLevelMarkerItem = (
  args: BuildLevelMarkerItemArgs,
): LevelIndicatorItem => {
  const {
    key,
    level,
    label,
    lineVariant,
    floodCategories,
    getCategoryForLevel,
  } = args;
  const { backgroundColor, useContrastText } = getLevelMarkerStyle(
    level,
    floodCategories,
    getCategoryForLevel,
  );

  return {
    key,
    level,
    backgroundColor,
    useContrastText,
    isMarker: true,
    hasCurrentLevelMarker: key === 'current-level',
    body: buildLevelMarkerBody({
      level,
      label,
      lineVariant,
      floodCategories,
      getCategoryForLevel,
    }),
  };
};

const buildCurrentLevelMarkerItem = (
  currentLevel: number,
  floodCategories: FloodCategoryLevel[],
  getCategoryForLevel: (targetLevel: number) => FloodCategory,
  t: TFunction,
): LevelIndicatorItem => {
  return buildLevelMarkerItem({
    key: 'current-level',
    level: currentLevel,
    label: t('waterLevel.currentLevel'),
    floodCategories,
    getCategoryForLevel,
  });
};

const buildForecastPeakMarkerItem = (
  forecastPeakLevel: number,
  floodCategories: FloodCategoryLevel[],
  getCategoryForLevel: (targetLevel: number) => FloodCategory,
  t: TFunction,
): LevelIndicatorItem => {
  return buildLevelMarkerItem({
    key: 'forecast-peak-level',
    level: forecastPeakLevel,
    label: t('waterLevel.forecastedLevel'),
    lineVariant: 'dashed',
    floodCategories,
    getCategoryForLevel,
  });
};

const impactMatchesLevel = (
  impactLevel: number,
  level: number | undefined,
): boolean =>
  level !== undefined && waterLevelsEqualForDisplay(impactLevel, level);

export const getLevelIndicatorItems = (
  args: GetLevelIndicatorItemsArgs,
): LevelIndicatorItem[] => {
  const { floodImpacts, floodCategories, currentLevel, forecastPeakLevel, t } =
    args;
  const getCategoryForLevel = (level: number): FloodCategory =>
    getFloodCategoryForLevel(level, floodCategories);
  const existingImpactLevels = new Set(floodImpacts.map((i) => i.level));

  const missingCategoryImpacts: FloodImpact[] = floodCategories
    .filter(
      (floodCategory) =>
        floodCategory.category !== 'none' &&
        !existingImpactLevels.has(floodCategory.level),
    )
    .map((floodCategory) => ({
      level: floodCategory.level,
      statement: t(
        `waterLevel.weatherLevelsChart.levelAreaDescriptions.${floodCategory.category}`,
        { defaultValue: floodCategory.category },
      ),
    }));

  const sortedImpacts = [...floodImpacts, ...missingCategoryImpacts].sort(
    (a, b) => a.level - b.level,
  );

  const shouldShowForecastPeak = shouldDisplayWaterLevelForecast({
    floodCategories,
    currentLevel,
    forecastPeakLevel,
  });

  let currentMergedIntoImpact = false;
  let forecastMergedIntoImpact = false;

  const impactItems: LevelIndicatorItem[] = sortedImpacts.map((impact) => {
    const category: FloodCategory = getCategoryForLevel(impact.level);
    const isThreshold = floodCategories.some((fc) =>
      waterLevelsEqualForDisplay(fc.level, impact.level),
    );
    const backgroundColor = floodCategoryColorsSolid[category];

    let markerSlot: ReactNode | undefined;
    let hasCurrentLevelMarker = false;

    if (
      !currentMergedIntoImpact &&
      impactMatchesLevel(impact.level, currentLevel)
    ) {
      currentMergedIntoImpact = true;
      hasCurrentLevelMarker = true;
      markerSlot = buildLevelMarkerBody({
        level: impact.level,
        label: t('waterLevel.currentLevel'),
        floodCategories,
        getCategoryForLevel,
      });
    } else if (
      !forecastMergedIntoImpact &&
      shouldShowForecastPeak &&
      impactMatchesLevel(impact.level, forecastPeakLevel)
    ) {
      forecastMergedIntoImpact = true;
      markerSlot = buildLevelMarkerBody({
        level: impact.level,
        label: t('waterLevel.forecastedLevel'),
        lineVariant: 'dashed',
        floodCategories,
        getCategoryForLevel,
      });
    }

    return {
      key: `impact-${impact.level}`,
      level: impact.level,
      backgroundColor,
      useContrastText: true,
      isThreshold,
      body: <Typography variant="body1">{impact.statement}</Typography>,
      markerSlot,
      hasCurrentLevelMarker,
    };
  });

  let items = impactItems;

  if (currentLevel !== undefined && !currentMergedIntoImpact) {
    items = insertLevelIndicatorItem(
      items,
      buildCurrentLevelMarkerItem(
        currentLevel,
        floodCategories,
        getCategoryForLevel,
        t,
      ),
    );
  }

  if (shouldShowForecastPeak && !forecastMergedIntoImpact) {
    items = insertLevelIndicatorItem(
      items,
      buildForecastPeakMarkerItem(
        forecastPeakLevel!,
        floodCategories,
        getCategoryForLevel,
        t,
      ),
    );
  }

  return items;
};
